import { useEffect, useState } from 'react'

export interface RequirementsFieldProps {
  /** Persisted household.house_requirements_raw — the must-have half. */
  mustHave: string | null
  /** Persisted household.house_nice_to_have_raw — the preferences half. */
  niceToHave: string | null
  disabled?: boolean
  onMustHaveChange: (value: string) => void
  onNiceToHaveChange: (value: string) => void
}

/**
 * Raw text in, raw text out. No parsing, no validation, no structuring — LLM
 * structuring into one row per criterion is phase 3.
 *
 * TWO BOXES, not one (#36: "not all needs are the same"). One textarea made
 * "a table that seats all of us" and "a hot tub would be fun" indistinguishable,
 * so an organizer weighing two houses could not tell which of a household's
 * wishes actually mattered.
 *
 * This is the one place two textareas earn their keep. The pair that was removed
 * from the location section split by TOPIC — house vs nearby — which made people
 * guess where a thing belonged. These split by STRENGTH on one topic, which is
 * the question being asked, and the labels answer it without ambiguity.
 */
export function RequirementsField({
  mustHave,
  niceToHave,
  disabled = false,
  onMustHaveChange,
  onNiceToHaveChange,
}: RequirementsFieldProps) {
  const [mustDraft, setMustDraft] = useState(mustHave ?? '')
  const [niceDraft, setNiceDraft] = useState(niceToHave ?? '')

  // Re-sync when the row reloads or an organizer switches household. Not on every
  // keystroke: the saved value lags the draft by design, because the parent
  // debounces the write.
  useEffect(() => {
    setMustDraft(mustHave ?? '')
  }, [mustHave])

  useEffect(() => {
    setNiceDraft(niceToHave ?? '')
  }, [niceToHave])

  return (
    <div>
      <label className="spz-field">
        <span className="spz-label">Must have</span>
        <textarea
          className="spz-textarea"
          rows={4}
          placeholder="e.g. 7+ bedrooms, a table that seats everyone, a ground-floor bedroom…"
          value={mustDraft}
          disabled={disabled}
          onChange={(e) => {
            setMustDraft(e.target.value)
            onMustHaveChange(e.target.value)
          }}
          onBlur={() => onMustHaveChange(mustDraft)}
        />
      </label>
      <p className="spz-hint">
        The things that would rule a house out for you. Plain words are fine &mdash; we sort it
        out later. Nothing here vetoes a house on its own; it tells the organizers what&rsquo;s
        close to a deal-breaker.
      </p>

      <label className="spz-field" style={{ marginTop: '0.9rem' }}>
        <span className="spz-label">
          Nice to have <span className="spz-hint">(optional)</span>
        </span>
        <textarea
          className="spz-textarea"
          rows={3}
          placeholder="e.g. a hot tub, a fire pit, a decent kitchen…"
          value={niceDraft}
          disabled={disabled}
          onChange={(e) => {
            setNiceDraft(e.target.value)
            onNiceToHaveChange(e.target.value)
          }}
          onBlur={() => onNiceToHaveChange(niceDraft)}
        />
      </label>
      <p className="spz-hint">
        Would make the week better, but you&rsquo;d live without it.
      </p>
    </div>
  )
}

export default RequirementsField
